import { nextTick, onMounted, onUnmounted, watch } from 'vue';
import { getGeoItemMeta, syncGeoItemWithParent } from 'src/lib/geoItemHelper';
import { logMoviePlay } from 'src/lib/analyticsHelper';
import { type GeoItemsMeta } from 'src/models/geoItem';
import { getCurrentLocationDisplayInfo, isTimeDispChanged, preCalculateMovieVals } from 'src/lib/moviePlayerHelper';
import type {
  CompareModeEmit,
  CompareModeProps,
  LeftRight,
  SwitchToFullModeParams,
} from 'src/models/moviePlayer';
import type { CustomGeoItemLayer } from 'src/models/user';
import type {
  useMoviePlayerCompareModeState,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeState';
import type {
  useMoviePlayerCompareModePlayback,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModePlayback';
import type {
  useMoviePlayerCompareModeSeek,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeSeek';
import type {
  useMoviePlayerCompareModeMapLinkage,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeMapLinkage';
import type {
  useMoviePlayerCompareModeMovieList,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeMovieList';
import type {
  useMoviePlayerCompareModeTagModal,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeTagModal';
import {
  useMoviePlayerCompareModeDateRangeSearch,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeDateRangeSearch';
import {
  useMoviePlayerCompareModeLayout,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeLayout';

const PLAYER_TYPE = 'compare';

interface SubComposables {
  playback: ReturnType<typeof useMoviePlayerCompareModePlayback>;
  seek: ReturnType<typeof useMoviePlayerCompareModeSeek>;
  map: ReturnType<typeof useMoviePlayerCompareModeMapLinkage>;
  movieList: ReturnType<typeof useMoviePlayerCompareModeMovieList>;
  tagModal: ReturnType<typeof useMoviePlayerCompareModeTagModal>;
}

interface MoviePlayerCompareModeContext {
  onCurrentMovieListIdChange: (lr: LeftRight) => void;
  searchMoviesByDateRange: (lr: LeftRight, dateOffset: number) => void;
  closeMoviePlayer: () => void;
  onVideoToolboxAreaClicked: (evt: Event) => void;
  shortcutKeyAction: (key: string) => void;
  switchToFullMode: () => void;
  sizes: ReturnType<typeof useMoviePlayerCompareModeLayout>['sizes'];
  styles: ReturnType<typeof useMoviePlayerCompareModeLayout>['styles'];
}

// MoviePlayerCompareMode/index.vue側で構築済みのstate・sub composableを受け取り、
// watch/onMounted等のオーケストレーションのみを提供する。
// 他のuse*は自分では呼び出さない。
export function useMoviePlayerCompareMode(
  props: Required<CompareModeProps>,
  emit: CompareModeEmit,
  playerState: ReturnType<typeof useMoviePlayerCompareModeState>,
  sub: SubComposables,
): MoviePlayerCompareModeContext {
  const {
    state,
    enqueueUiTask,
    refExtremeMap,
    refTopBarArea,
    refVideoArea,
    refControlAreaBoth,
    refControlAreaLeft,
    refControlAreaRight,
    refMetaInfoAreaLeft,
    refMetaInfoAreaRight,
    masters,
    resolveMastersPromise,
  } = playerState;
  const { playback, seek, map, movieList, tagModal } = sub;
  const layout = useMoviePlayerCompareModeLayout(state, {
    refExtremeMap,
    refTopBarArea,
    refVideoArea,
    videoAspectRatio: playback.videoAspectRatio,
    isCurrentMovieListBothReady: () => movieList.isCurrentMovieListBothReady,
  });
  onMounted(async() => {
    await resolveMastersPromise();
    window.addEventListener('resize', layout.onResize);
    await initializeDataManagers();
  });
  onUnmounted(() => {
    window.removeEventListener('resize', layout.onResize);
  });

  watch(() => props.movieLists, () => {
    const currentMovieLists = props.movieLists.map(ml => ({
      ...ml,
      isSelected: false, // 赤枠
      showTimeTexts: true, // 時間表示
    }));
    state.left.movieList.listSelection.currentMovieLists = currentMovieLists.map(ml => ({
      ...ml,
      isSelected: true, // 赤枠
    }));
    state.right.movieList.listSelection.currentMovieLists = currentMovieLists.map(ml => ({ ...ml }));
  });
  watch(() => props.movieListId, () => {
    if (!props.movieListId) return;

    const isInitialized = state.ui.isInitialized;
    state.left.movieList.listSelection.currentMovieListId = props.movieListId;
    onCurrentMovieListIdChange('left');
    if (isInitialized) {
      state.right.movieList.listSelection.currentMovieListId = props.movieListId;
      onCurrentMovieListIdChange('right');
    } else {
      nextTick(() => {
        state.right.movieList.listSelection.currentMovieListId = props.movieListId || '';
        onCurrentMovieListIdChange('right');
      });
    }
  });
  watch(() => state.left.geo.currentMovieCurrentDt, (newTimestamp, oldTimestamp) => {
    // 再生速度が変更できることを考慮し、
    // 時刻表示が変更されたタイミングで場所の表示を更新する
    if (!isTimeDispChanged(newTimestamp, oldTimestamp)) return;

    onCurrentMovieTimestampChange('left');
  });
  watch(() => state.right.geo.currentMovieCurrentDt, (newTimestamp, oldTimestamp) => {
    // 再生速度が変更できることを考慮し、
    // 時刻表示が変更されたタイミングで場所の表示を更新する
    if (!isTimeDispChanged(newTimestamp, oldTimestamp)) return;

    onCurrentMovieTimestampChange('right');
  });

  const initializeDataManagers = async() => {
    const layerListDefault: CustomGeoItemLayer[] = [
      { name: 'pin', dispName: 'pin', hideToggleCheckbox: false, isCreatable: false, isTabPanel: false, tabLabel: '' },
      {
        name: 'comment',
        dispName: '付箋',
        hideToggleCheckbox: false,
        isCreatable: false,
        isTabPanel: false,
        tabLabel: '',
      },
    ];
    state.geoItemsMeta.map = await getGeoItemMeta(layerListDefault);
  };
  // 動画切替後に「動画プレイヤー・地図を初期化 → リサイズ →
  // 地図更新 → 先頭シーク位置から再生開始」の順で
  // 走らせる必要がある一連の処理。この順序を変えると初期化前のDOM操作や
  // mapの座標計算が壊れるため、
  // 意味のある単位としてこの関数にまとめている。
  const runInitialPlaybackSequence = async(lr: LeftRight) => {
    await initializeViews();
    layout.onResize();
    map.updateMap();
    await playFromBeginning(lr, playback.findFirstSeekPoint(lr));
  };
  const runPlaybackSequenceForMovieListChange = async(lr: LeftRight) => {
    layout.onResize();
    map.updateMap();
    await playFromBeginning(lr, playback.findFirstSeekPoint(lr));
  };
  const onCurrentMovieListIdChange = (lr: LeftRight) => {
    for (const ml of Object.values(state[lr].movieList.listSelection.currentMovieLists)) {
      if (ml.id === state[lr].movieList.listSelection.currentMovieListId) {
        state[lr].movieList.listSelection.currentMovieList = ml;
        break;
      }
    }
    const currentMl = state[lr].movieList.listSelection.currentMovieList;
    if (!currentMl) return;
    state.movieSearch[lr].baseDate = currentMl.startTs;
    preCalculateMovieVals(currentMl, masters.kp.roadNameDispMap);
    movieList.setMoviePlayerControlDispVals(lr, currentMl);
    if (!state.ui.isInitialized) {
      state.ui.isInitialized = true;
      // 画面が見えてる状態でvideojsの初期化を行わないとvueの処理との兼ね合いで
      // DOMが変なところに挿入されたりする現象があったので、見えていることが
      // 分かっている状態からnextTickしてからinitialize
      nextTick(() => {
        enqueueUiTask(() => runInitialPlaybackSequence(lr));
      });
    } else {
      enqueueUiTask(() => runPlaybackSequenceForMovieListChange(lr));
    }
    if (refExtremeMap.value) {
      syncGeoItemWithParent(
        state.geoItemsMeta as GeoItemsMeta,
        props.parentGeoItemsMeta,
        refExtremeMap.value,
      );
    }
  };
  const dateRangeSearch = useMoviePlayerCompareModeDateRangeSearch(state, {
    getUIPlayState: playback.getUIPlayState,
    pauseUI: playback.pauseUI,
    isMovieSearchResultModeDetail: () => movieList.isMovieSearchResultModeDetail,
    isMovieSearchResultModeArea: () => movieList.isMovieSearchResultModeArea,
    remainingMovieRatesByDateRanges: () => movieList.remainingMovieRatesByDateRanges,
    parentMovieLayerMeta: () => props.parentMovieLayerMeta,
    parentMovieSearch: props.parentMovieSearch,
    onCurrentMovieListIdChange,
  });
  const onCurrentMovieTimestampChange = (lr: LeftRight) => {
    const currentMgi = state[lr].geo.currentMgi;
    const currentMovieCurrentDt = state[lr].geo.currentMovieCurrentDt;
    const currentMovieMgis = state[lr].movieList.current.currentMovie?.movieGeoIndices;
    if (!currentMgi || !currentMovieCurrentDt || !currentMovieMgis) {
      // mgiが取れない場合は地図上のピンの位置の更新はしない
      state[lr].geo.locationDisp = '';
      return;
    }
    const mgis = [ ...currentMovieMgis ];
    const nextMovieIdx = state[lr].movieList.current.currentMovieIdx + 1;
    const nextMovie = state[lr].movieList.listSelection.currentMovieList?.movies[nextMovieIdx];
    if (nextMovie) {
      mgis.push(...nextMovie.movieGeoIndices);
    }
    const locationDisplayInfo = getCurrentLocationDisplayInfo({
      currentTimestamp: currentMovieCurrentDt,
      movieGeoIndices: mgis,
      currentMgi: currentMgi,
      currentMgiIndex: state[lr].geo.currentMgiIdx,
    });
    state[lr].geo.locationDisp = locationDisplayInfo.movieMetaInfoLocationDisp;
    map.updateMapPinPosition(lr, {
      lat: locationDisplayInfo.location.lat.toString(),
      lon: locationDisplayInfo.location.lon.toString(),
    });
  };
  const initializeViews = () => {
    const p1 = playback.prepareVideoPlayers();
    const p2 = map.prepareMap();
    return Promise.all([p1, p2]);
  };
  const closeMoviePlayer = () => {
    setAllUIStateToDefault();
    emit('close');
  };
  const setAllUIStateToDefaultInner = async(lr: LeftRight) => {
    await playback.resetToDefault(lr);
    map.clearMapElemInfo(lr);
    const controlArea = lr === 'left' ? refControlAreaLeft.value : refControlAreaRight.value;
    const metaInfoArea = lr === 'left' ? refMetaInfoAreaLeft.value : refMetaInfoAreaRight.value;
    if (controlArea) {
      controlArea.resetVolume();
    }
    if (metaInfoArea) {
      metaInfoArea.setAllUIStateToDefault();
    }
  };
  const setAllUIStateToDefault = async() => {
    await Promise.all([setAllUIStateToDefaultInner('left'), setAllUIStateToDefaultInner('right')]);
    state.both.isPlayBackward = false;
    state.both.isPlaying = false;
    if (refControlAreaBoth.value) {
      refControlAreaBoth.value.resetVolume();
    }
  };
  const playFromBeginning = async(lr: LeftRight, seekPoint: number) => {
    const playSpeed = state[lr].playback.currentPlaySpeed;
    await seek.seekMovie(lr, seekPoint);
    await setAllUIStateToDefaultInner(lr);
    await playback.setPlaySpeed(lr, playSpeed);
    playback.playUI(lr);
    if (lr === 'left') { logMoviePlay(PLAYER_TYPE); }
  };
  /* smartphoneの場合はホバーで表示/非表示切替できないので、tapで下記になると思う
  toggleVideoToolbox(vid) {
    const flg = state.showVideoToolbox[vid]
    for (const prop in state.showVideoToolbox) {
      state.showVideoToolbox[prop] = false
    }
    state.showVideoToolbox[vid] = !flg
  },
  */
  const onVideoToolboxAreaClicked = (evt: Event) => {
    // toolboxの黒いバー内が押された場合はtoggleVideoToolboxが
    // 呼ばれないようにする
    evt.preventDefault();
    evt.stopImmediatePropagation();
  };
  const shortcutKeyAction = (key: string) => {
    switch (key) {
      case 'space': {
        // 左を正として両方操作
        const isLeftPlaying = state.left.playback.isPlaying;
        if (isLeftPlaying) {
          playback.pauseUI('both');
        } else {
          playback.playUI('both');
        }
        break;
      }
      case 'left':
        playback.tickMovieBackward('both');
        break;
      case 'right':
        playback.tickMovieForward('both');
        break;
      case 'escape':
        if (tagModal.movieTagModal.show) {
          tagModal.movieTagModal.show = false;
        }
        break;
    }
  };
  const switchToFullMode = () => {
    setAllUIStateToDefault();
    const params: SwitchToFullModeParams = {
      leftMovieListId: state.left.movieList.listSelection.currentMovieListId,
      rightMovieListId: state.right.movieList.listSelection.currentMovieListId,
    };
    emit('switch-to-full-mode', params);
  };
  return {
    onCurrentMovieListIdChange,
    searchMoviesByDateRange: dateRangeSearch.searchMoviesByDateRange,
    closeMoviePlayer,
    onVideoToolboxAreaClicked,
    shortcutKeyAction,
    switchToFullMode,
    sizes: layout.sizes,
    styles: layout.styles,
  };
}
